import { useSetAtom } from 'jotai';
import { domain } from 'modules/data';
import moment from 'moment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { useCallback } from 'react';
import { DomainEntitiesOcrOcrStatus, GetOcrSuggests, useGetOcrStatus } from 'shared/data/webApp/v2';
import { checkOcrStatusTimeoutIdSelector, ocrIsLoadingState } from 'shared/states';

export const useCheckOcrStatus = (
    request: domain.Request | null,
    attachmentId?: string,
    successCallback?: (data: GetOcrSuggests['response']) => void | Promise<void>,
    errorCallback?: (errorMessage?: string) => void
) => {
    const setCheckOcrStatusTimeoutId = useSetAtom(checkOcrStatusTimeoutIdSelector);
    const setOcrIsLoading = useSetAtom(ocrIsLoadingState);

    const {
        data: ocrStatus,
        refetch: refetchOcrStatus,
        isError,
        isFetched,
    } = useGetOcrStatus(
        {
            path: {
                companyId: request?.companyId,
                requestId: request?.id,
                attachmentId: attachmentId,
            },
        },
        {
            enabled: false,
        }
    );

    const { refetch: refetchOcr } = useOcrData(request, attachmentId, { enabled: false });

    const checkOcrStatus = useCallback(
        async (ocrStartTime?: string, shouldCheckImmediately: boolean = false) => {
            if (!shouldCheckImmediately) {
                const timeoutId = setTimeout(
                    () => checkOcrStatus(ocrStartTime, true),
                    getPollingInterval(ocrStartTime)
                );

                setCheckOcrStatusTimeoutId(timeoutId);
            } else {
                setOcrIsLoading(true);
                setCheckOcrStatusTimeoutId(null);

                const { data: ocrStatus, isError } = await refetchOcrStatus();

                if (ocrStatus?.status === DomainEntitiesOcrOcrStatus.Skipped) {
                    setOcrIsLoading(false);

                    return;
                }

                if (
                    !isError &&
                    (ocrStatus?.status === DomainEntitiesOcrOcrStatus.InProgress ||
                        ocrStatus?.status === DomainEntitiesOcrOcrStatus.Requested)
                ) {
                    const timeoutId = setTimeout(
                        () => checkOcrStatus(ocrStartTime, true),
                        getPollingInterval(ocrStartTime)
                    );

                    setCheckOcrStatusTimeoutId(timeoutId);
                } else {
                    const { data } = await refetchOcr();

                    if (ocrStatus?.status === DomainEntitiesOcrOcrStatus.Succeeded && data) {
                        ocrStatus && successCallback && (await successCallback(data));
                    }

                    if (isError || (ocrStatus?.status === DomainEntitiesOcrOcrStatus.Failed && data)) {
                        errorCallback && errorCallback(data?.failureReason);
                    }

                    setOcrIsLoading(false);
                }
            }
        },
        [setCheckOcrStatusTimeoutId, setOcrIsLoading, refetchOcrStatus, refetchOcr, successCallback, errorCallback]
    );

    const isOcrAttachmentInProgress =
        !isError &&
        (ocrStatus?.status === DomainEntitiesOcrOcrStatus.InProgress ||
            ocrStatus?.status === DomainEntitiesOcrOcrStatus.Requested);

    return {
        checkOcrStatus,
        isOcrAttachmentInProgress,
        isFetched,
    };
};

// The values for polling intervals were taken from the requirements described in this task:
// https://approvalmax.atlassian.net/browse/AM-25891
const getPollingInterval = (ocrStartTime: string | undefined) => {
    const now = moment.utc();
    const start = moment.utc(ocrStartTime);
    const diffSeconds = now.diff(start, 'seconds');

    if (diffSeconds < 4) return (4 - diffSeconds) * 1000;
    if (diffSeconds < 30) return 2000;
    if (diffSeconds < 60) return 6000;

    return 20000;
};
